import { useMutation, useQuery } from "@tanstack/react-query";
import { displayWarning, displaySuccess } from "components/Toast/toast.component";
import BrandedLoading from "components/loading/branded-loading.component";
import {
  getCareerSelectionList,
  unlockOnboard,
  unlockNextGlimpse,
  startOnBoard,
} from "services/on-board";
import { useLocation, useNavigate } from "react-router-dom";
import { useCallback, useEffect, useMemo, useState } from "react";
import Button from "components/button/button";
import PillTab from "components/pill-tab/pill-tab";
import CareerItem from "components/career-item/career-item.component";

// --- Local Types ---

type CareerSelectionItem = {
  id: string;
  name: string;
  icon: string;
  icon_url: string;
  status: string;
  is_unlocked?: boolean;
};

type SelectableCareerCardProps = {
  career: CareerSelectionItem;
  selectionIndex: number;
  isDisabled: boolean;
  onSelect: (id: string) => void;
  alreadyUnlocked?: boolean;
};

// --- Constants ---

const MAX_SELECTIONS = 5;

const ALPHA_FILTERS = [
  { label: "A-B", range: ["A", "B"] },
  { label: "C-D", range: ["C", "D"] },
  { label: "E-I", range: ["E", "F", "G", "H", "I"] },
  { label: "J-N", range: ["J", "K", "L", "M", "N"] },
  { label: "O-P", range: ["O", "P"] },
  { label: "Q-Z", range: ["Q", "R", "S", "T", "U", "V", "W", "X", "Y", "Z"] },
];

// --- Helpers ---

const extractErrorMessage = (err: any): string =>
  err?.response?.data?.detail ||
  err?.response?.data?.career_ids?.[0] ||
  "Unable to unlock careers";

// --- Sub-components ---

const SelectableCareerCard = ({
  career,
  selectionIndex,
  isDisabled,
  onSelect,
  alreadyUnlocked = false,
}: SelectableCareerCardProps) => {
  const isSelected = selectionIndex !== -1;

  return (
    <button
      type="button"
      aria-pressed={isSelected}
      aria-label={alreadyUnlocked ? `${career.name} (already unlocked)` : career.name}
      disabled={isDisabled}
      className={`relative border-none bg-transparent p-0 transition-all ${
        isDisabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer"
      }`}
      onClick={() => !isDisabled && onSelect(career.id)}
    >
      <div className={`transition-all ${isSelected ? "ring-[3px] ring-action ring-offset-2 rounded-xl" : ""}`}>
        <CareerItem
          id={career.id}
          name={career.name}
          icon_url={career.icon_url}
          pressed={() => {}}
        />
      </div>
      {isSelected && (
        <span className="absolute -bottom-3 left-1/2 -translate-x-1/2 bg-action text-white text-[10px] font-bold px-3 py-1 rounded-full shadow-md whitespace-nowrap">
          Selected
        </span>
      )}
      {alreadyUnlocked && !isSelected && (
        <span className="absolute -bottom-3 left-1/2 -translate-x-1/2 bg-text-muted text-white text-[10px] font-bold px-3 py-1 rounded-full shadow-md whitespace-nowrap">
          Already unlocked
        </span>
      )}
    </button>
  );
};

// --- Main Screen ---

type PickerMode = "onboarding" | "user-careers-unlock-next" | "prioritize-unlock-next";

const resolveModeFromPath = (pathname: string): PickerMode => {
  if (pathname.includes("/user-careers/unlock-next")) return "user-careers-unlock-next";
  if (pathname.includes("/prioritize/unlock-next")) return "prioritize-unlock-next";
  return "onboarding";
};

const VIDEO_ROUTE_BY_MODE: Record<PickerMode, string> = {
  "onboarding": "/on-boarding/start",
  "user-careers-unlock-next": "/user-careers/unlock-next/start",
  "prioritize-unlock-next": "/prioritize/unlock-next/start",
};

const CareerPickerScreen = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const mode = useMemo(() => resolveModeFromPath(pathname), [pathname]);
  const isOnboarding = mode === "onboarding";

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [activeFilter, setActiveFilter] = useState<string | null>("A-B");

  // Onboarding-only: if the user already has careers unlocked we shouldn't
  // be on this screen at all — skip straight to videos. The unlock-next mode
  // intentionally lets the user back through the picker even though they
  // have existing careers.
  const { data: existingCareers } = useQuery({
    queryKey: ["CheckExistingCareersForPicker"],
    queryFn: () => startOnBoard(1),
    enabled: isOnboarding,
    refetchOnWindowFocus: false,
    retry: 0,
  });

  useEffect(() => {
    if (!isOnboarding) return;
    if (existingCareers && existingCareers.count > 0) {
      console.log("[career-picker] Careers already unlocked, skipping to videos");
      navigate("/on-boarding/start", { replace: true });
    }
  }, [existingCareers, navigate, isOnboarding]);

  const { data: careers, isLoading, isError, refetch } = useQuery({
    queryKey: ["careerSelectionList", mode],
    queryFn: getCareerSelectionList,
    refetchOnWindowFocus: false,
    retry: 0,
  });

  const filteredCareers = useMemo(() => {
    if (!careers) return [];
    if (!activeFilter) return careers;
    const filter = ALPHA_FILTERS.find((f) => f.label === activeFilter);
    if (!filter) return careers;
    return careers.filter((c: CareerSelectionItem) =>
      filter.range.includes(c.name.charAt(0).toUpperCase())
    );
  }, [careers, activeFilter]);

  const unlockMutation = useMutation({
    mutationKey: ["unlockCareers", mode],
    mutationFn: (careerIds?: string[]) =>
      isOnboarding ? unlockOnboard(careerIds) : unlockNextGlimpse(careerIds),
    retry: 0,
    gcTime: 0,
    onSuccess: (res: any) => {
      // Onboarding endpoint returns no unlock_status; unlock-next does.
      if (!isOnboarding && res && res.unlock_status === false) {
        displayWarning(res.unlock_message || "Unable to unlock careers");
        return;
      }
      if (isOnboarding) localStorage.setItem("hasVisited", "true");
      navigate(VIDEO_ROUTE_BY_MODE[mode]);
    },
    onError: (err: any) => {
      displayWarning(extractErrorMessage(err));
    },
  });

  // Available pool = locked + published careers; max selectable adapts when fewer remain
  const lockedCount = useMemo(() => {
    if (!careers) return MAX_SELECTIONS;
    return careers.filter(
      (c: CareerSelectionItem) =>
        !c.is_unlocked && c.status === "published",
    ).length;
  }, [careers]);

  const effectiveMax = isOnboarding
    ? MAX_SELECTIONS
    : Math.min(MAX_SELECTIONS, lockedCount || MAX_SELECTIONS);

  const handleSelectCareer = useCallback(
    (id: string) => {
      setSelectedIds((prev) => {
        if (prev.includes(id)) return prev.filter((cId) => cId !== id);
        if (prev.length >= effectiveMax) return prev;
        return [...prev, id];
      });
    },
    [effectiveMax],
  );

  const handleContinue = () => {
    if (selectedIds.length === 0 || selectedIds.length > effectiveMax) return;
    if (isOnboarding && selectedIds.length !== MAX_SELECTIONS) return;
    unlockMutation.mutate(selectedIds, {
      onSuccess: (res: any) => {
        // Mirror the hook-level guard: when the BE returns a logical failure
        // (HTTP 200 + unlock_status: false), the hook-level onSuccess has
        // already shown the warning. Suppress the misleading success toast.
        if (!isOnboarding && res && res.unlock_status === false) return;
        displaySuccess("Your selections have been saved!");
      },
    });
  };

  const handleChooseForMe = () => {
    unlockMutation.mutate(undefined, {
      onSuccess: (res: any) => {
        if (!isOnboarding && res && res.unlock_status === false) return;
        displaySuccess("We've picked some great careers for you!");
      },
    });
  };

  const isUnlocking = unlockMutation.isPending;
  const isFull = selectedIds.length >= effectiveMax;
  const canContinue =
    selectedIds.length === effectiveMax &&
    selectedIds.length > 0 &&
    !isUnlocking;

  // Get selected career objects for the bottom bar avatars
  const selectedCareers = useMemo(() => {
    if (!careers) return [];
    return selectedIds
      .map((id) => careers.find((c: CareerSelectionItem) => c.id === id))
      .filter(Boolean) as CareerSelectionItem[];
  }, [careers, selectedIds]);

  if (isLoading) {
    return <BrandedLoading text="Loading careers..." />;
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[calc(100vh-56px)] bg-white gap-4">
        <p className="text-body text-text-heading">Error Occurred</p>
        <Button variant="primary" onClick={() => refetch()}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col justify-between bg-white min-h-[calc(100vh-56px)] max-w-[1280px] mx-auto w-full py-8">
      {/* Top bar */}
      <div className="flex items-center justify-between px-6">
        <Button
          type="button"
          variant="secondary"
          onClick={() => navigate(-1)}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Back
        </Button>
        {isOnboarding && (
          <span className="text-body-sm-emphasis text-text-muted">Step 1 of 3</span>
        )}
        <Button
          type="button"
          variant="secondary"
          onClick={handleChooseForMe}
          disabled={isUnlocking}
        >
          Choose for me
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M6 4L10 8L6 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </Button>
      </div>

      {/* Main content — scrollable */}
      <div className="flex-1 overflow-auto px-4">
        <div className="flex flex-col items-center w-full max-w-[960px] mx-auto gap-8">
          {/* Heading */}
          <header className="flex flex-col items-center gap-2 text-center">
            <h1 className="text-display-lg text-brand">
              {isOnboarding
                ? "What careers would you like to explore first?"
                : "Which careers would you like to unlock next?"}
            </h1>
            <p className="text-body text-text-heading max-w-[560px] text-center">
              {isOnboarding
                ? `Pick five to start, and we'll show you their introduction videos next. Not sure where to begin? Select "Choose for me."`
                : `Pick five new careers to add to your list. Already-unlocked careers are dimmed. Not sure where to begin? Select "Choose for me."`}
            </p>
          </header>

          {/* Alpha filter pills */}
          <div className="flex flex-wrap items-center justify-center gap-2">
            {ALPHA_FILTERS.map((filter) => (
              <PillTab
                key={filter.label}
                label={filter.label}
                isActive={activeFilter === filter.label}
                onClick={() =>
                  setActiveFilter((prev) =>
                    prev === filter.label ? null : filter.label
                  )
                }
              />
            ))}
          </div>

          {/* Career grid */}
          <div className="flex flex-wrap justify-center gap-6">
            {filteredCareers.map((career: CareerSelectionItem) => {
              const alreadyUnlocked = !!career.is_unlocked;
              const isSelected = selectedIds.includes(career.id);
              return (
                <SelectableCareerCard
                  key={career.id}
                  career={career}
                  selectionIndex={selectedIds.indexOf(career.id)}
                  isDisabled={
                    alreadyUnlocked || (isFull && !isSelected)
                  }
                  alreadyUnlocked={alreadyUnlocked}
                  onSelect={handleSelectCareer}
                />
              );
            })}
          </div>
        </div>
      </div>

      {/* Bottom selection bar */}
      <div className="sticky bottom-0 bg-white px-6 py-4">
        <div className="flex items-center justify-end gap-4">
          {/* Selected avatars */}
          <div className="flex items-center gap-3">
            <div className="flex -space-x-2">
              {selectedCareers.map((career) => (
                <img
                  key={career.id}
                  src={career.icon_url}
                  alt={career.name}
                  className="w-9 h-9 rounded-full border-2 border-white object-cover"
                />
              ))}
            </div>
            <span className="text-body-sm text-text-heading whitespace-nowrap">
              {selectedIds.length} of {effectiveMax} selected
            </span>
          </div>

          {/* Continue button */}
          <Button
            type="button"
            variant="primary"
            onClick={handleContinue}
            disabled={!canContinue}
          >
            {isUnlocking ? "Loading..." : "Continue"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CareerPickerScreen;
